import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useCampaignSheet, useCampaignWorld } from '../../domains/campaigns/queries'
import { useCampaignEditing } from '../../domains/campaigns/useCampaignEditing'
import { CityCard } from '../../domains/campaigns/sections/CityCard'
import { FacesCard } from '../../domains/campaigns/sections/FacesCard'
import { FactionsCard } from '../../domains/campaigns/sections/FactionsCard'
import { LocationsCard } from '../../domains/campaigns/sections/LocationsCard'
import { Banner, Button, Card } from '../../design/components'
import styles from './campaigns.module.css'

/**
 * Design spec §5.4 — the city as a character: its theme at the top, the Faces who
 * live in it, the Factions that run it, and the Places it happens in.
 *
 * **Two reads, deliberately.** The world graph (city, factions, locations) is its own
 * endpoint because it is deep and most campaign opens never show it; the campaign
 * sheet is fetched alongside it here for one reason — the roster. A faction member and
 * a place's cast must already be in play, so the pickers need the campaign's
 * characters, which live in the *sheet's* `characters` section rather than the world's.
 * That is also what feeds the Faces card.
 *
 * Both reads land in separate cache entries and every mutation patches both, each with
 * only the sections it holds (see domains/campaigns/mutations.ts). So adding a faction
 * updates the world's faction list *and* the campaign page's faction count, with no
 * refetch of either.
 */
export function CityPage() {
  const params = useParams<{ id: string }>()
  const campaignId = Number(params.id)

  const world = useCampaignWorld(campaignId)
  const campaign = useCampaignSheet(campaignId)

  const editing = useCampaignEditing(campaignId, {
    isGameMaster: world.data?.isGameMaster === true,
    isMember: world.data !== undefined,
    canAddCharacter: campaign.data?.sections.core?.canAddCharacter === true,
  })

  if (Number.isNaN(campaignId)) {
    return <CityMessage campaignId={campaignId} message="That campaign id is not a number." />
  }

  if (world.isPending) {
    return <CityMessage campaignId={campaignId} message="Loading…" subtitle="FETCHING THE CITY" />
  }

  if (world.isError) {
    const notFound =
      world.error instanceof ApiError &&
      (world.error.kind === 'not-found' || world.error.kind === 'forbidden')

    return (
      <CityMessage
        campaignId={campaignId}
        message={
          notFound ? 'No such campaign, or it is not one of yours.' : 'Could not load this city.'
        }
        tone={notFound ? 'info' : 'error'}
      />
    )
  }

  const city = world.data.sections.city
  // The roster is only as good as the campaign read beside it; while that is still in
  // flight the pickers simply have nothing to offer, which is honest — the alternative
  // is offering a list that is about to change.
  const roster = campaign.data?.sections.characters?.characters ?? []

  return (
    <AppShell
      title={city?.name ?? 'The City'}
      subtitle={subtitleFor(city?.factionCount, city?.locationCount)}
    >
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      <CityCard city={city} editing={editing} />
      <FacesCard roster={roster} />
      <FactionsCard factions={world.data.sections.factions} roster={roster} editing={editing} />
      <LocationsCard
        campaignId={campaignId}
        locations={world.data.sections.locations}
        roster={roster}
        editing={editing}
      />

      <Link to={`/campaigns/${campaignId}`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          Back to the campaign
        </Button>
      </Link>
    </AppShell>
  )
}

function CityMessage({
  campaignId,
  message,
  subtitle,
  tone = 'info',
}: {
  campaignId: number
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="The City" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link
        to={Number.isNaN(campaignId) ? '/campaigns' : `/campaigns/${campaignId}`}
        className={styles.backLink}
      >
        <Button variant="secondary" fullWidth>
          {Number.isNaN(campaignId) ? 'All campaigns' : 'Back to the campaign'}
        </Button>
      </Link>
    </AppShell>
  )
}

/** e.g. "2 FACTIONS · 5 PLACES". */
function subtitleFor(factions: number | undefined, places: number | undefined): string | undefined {
  if (factions === undefined && places === undefined) return undefined

  const parts: string[] = []
  if (factions !== undefined) parts.push(factions === 1 ? '1 faction' : `${factions} factions`)
  if (places !== undefined) parts.push(places === 1 ? '1 place' : `${places} places`)

  return parts.join(' · ')
}
